import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from './config';
import { CARD } from './ui';
import { canSeeBillingAmounts, canViewBilling } from './roles';
import AccountView from './billing/AccountView';
import TeamUsageView from './billing/TeamUsageView';
import RevenueOverview from './billing/RevenueOverview';
import PlanModal from './billing/PlanModal';
import { useInvoiceActions } from './billing/InvoiceParts';

const Shell = ({ title, subtitle, back, children }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        {back}
        <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
        {subtitle && <p className="text-lg text-gray-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  </div>
);

// One company's bill, loaded for an admin (their own) or a dev (any, by id).
// Devs also get the plan and invoice actions here.
const CompanyBilling = ({ companyId, dev, onLoaded }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const query = companyId ? `?company_id=${companyId}` : '';
    axios
      .get(`${API_BASE_URL}/api/billing/account${query}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
        onLoaded?.(res.data.company);
      })
      .catch((err) => !cancelled && setError(err.response?.data?.msg || 'Could not load billing.'));
    return () => {
      cancelled = true;
    };
  }, [companyId, version, onLoaded]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const actions = useInvoiceActions(reload);

  const savePlan = async (payload) => {
    try {
      await axios.put(`${API_BASE_URL}/api/billing/subscriptions/${data.company.id}`, payload);
      setPlanOpen(false);
      reload();
      return null;
    } catch (err) {
      return err.response?.data?.msg || 'Could not save the plan.';
    }
  };

  if (error) return <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-red-300">{error}</div>;
  if (!data) return <div className={`${CARD} py-20 text-center text-gray-400`}>Loading billing…</div>;

  return (
    <>
      {dev && (
        <>
          <PlanModal
            company={planOpen ? { ...data.company, subscription: data.subscription } : null}
            plans={data.plans}
            onConfirm={savePlan}
            onCancel={() => setPlanOpen(false)}
          />
          {actions.modal}
        </>
      )}
      <AccountView data={data} actions={dev ? actions : null} onChangePlan={() => setPlanOpen(true)} />
    </>
  );
};

const ManagerBilling = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/billing/account`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.msg || 'Could not load your plan.'));
  }, []);

  if (error) return <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-red-300">{error}</div>;
  if (!data) return <div className={`${CARD} py-20 text-center text-gray-400`}>Loading…</div>;
  return <TeamUsageView data={data} />;
};

const BackLink = () => (
  <Link to="/billing" className="inline-flex items-center gap-1 text-sm text-purple-300 hover:text-purple-200 mb-3">
    <span aria-hidden="true">←</span> All customers
  </Link>
);

// Who sees what: inspectors nothing, managers their plan and their team's
// usage with no money, admins their company's whole bill, and devs every
// company plus the revenue overview. The API enforces the same split; this
// only decides which screen to draw.
const BillingPage = () => {
  const { user } = useAuth();
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);

  if (!canViewBilling(user.role)) {
    return (
      <Shell title="Billing">
        <div className={`${CARD} p-8 text-center text-gray-300`}>Billing is handled by your company's admins.</div>
      </Shell>
    );
  }

  if (!canSeeBillingAmounts(user.role)) {
    return (
      <Shell title="Plan & usage" subtitle="Your company's plan and what your team used this month.">
        <ManagerBilling />
      </Shell>
    );
  }

  if (user.role === 'dev' && !companyId) {
    return (
      <Shell title="Billing" subtitle="Revenue, receivables and plans across every customer.">
        <RevenueOverview />
      </Shell>
    );
  }

  return (
    <Shell
      title={user.role === 'dev' ? company?.name || 'Billing' : 'Billing'}
      subtitle={
        user.role === 'dev'
          ? 'This company’s bill, as its admins see it.'
          : 'Your plan, this month’s usage, and every invoice.'
      }
      back={user.role === 'dev' ? <BackLink /> : null}
    >
      <CompanyBilling companyId={user.role === 'dev' ? companyId : null} dev={user.role === 'dev'} onLoaded={setCompany} />
    </Shell>
  );
};

export default BillingPage;
